import React from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import {
  GiMusicalNotes,
  GiGuitar,
  GiDrum,
  GiMicrophone,
  GiFlute,
} from "react-icons/gi";
import { motion } from "framer-motion";

const instruments = [
  {
    icon: <GiGuitar size={28} />,
    title: "Guitar",
    color: "#f4c842",
    border: "rgba(244,200,66,0.2)",
  },
  {
    icon: <GiDrum size={28} />,
    title: "Drums",
    color: "#f97316",
    border: "rgba(249,115,22,0.2)",
  },
  {
    icon: <GiMicrophone size={28} />,
    title: "Vocal",
    color: "#3b82f6",
    border: "rgba(59,130,246,0.2)",
  },
  {
    icon: <GiMusicalNotes size={28} />,
    title: "Keyboard",
    color: "#22c55e",
    border: "rgba(34,197,94,0.2)",
  },
  {
    icon: <GiMusicalNotes size={28} />,
    title: "Tabla",
    color: "#8b5cf6",
    border: "rgba(139,92,246,0.2)",
  },
  {
    icon: <GiMusicalNotes size={28} />,
    title: "Harmonium",
    color: "#ec4899",
    border: "rgba(236,72,153,0.2)",
  },
  {
    icon: <GiFlute size={28} />,
    title: "Flute",
    color: "#06b6d4",
    border: "rgba(6,182,212,0.2)",
  },
  {
    icon: <GiMusicalNotes size={28} />,
    title: "Dholak",
    color: "#ef4444",
    border: "rgba(239,68,68,0.2)",
  },
];

const MusicRoom = () => {
  return (
    <section className="relative min-h-screen py-24 px-6 md:px-16 pt-36 md:pt-40 overflow-hidden">
      <div
        className="absolute top-0 right-0 w-96 h-96 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(236,72,153,0.07) 0%, transparent 70%)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-80 h-80 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <Link
          to="/facilities"
          className="inline-block text-sm font-bold tracking-wide mb-8 transition-colors duration-200"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Facilities
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          Music Room
        </h1>
        <div className="cosmic-divider mb-6" />

        <p
          className="mb-12 max-w-2xl"
          style={{
            fontFamily: "Nunito, sans-serif",
            color: "#8899b8",
            fontSize: "0.95rem",
            lineHeight: "1.8",
          }}
        >
          The Music Room is where creativity and rhythm come alive. Students are
          introduced to Indian and Western music traditions, trained in both
          vocals and instruments, and encouraged to express themselves through
          musical exploration.
        </p>

        {/* Instrument cards */}
        <h2
          className="mb-6 font-bold"
          style={{
            fontFamily: "Cinzel, serif",
            color: "#f0f5ff",
            fontSize: "1.1rem",
          }}
        >
          Instruments
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5 mb-14">
          {instruments.map((inst, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="glass-card p-5 flex flex-col items-center gap-3 text-center transition-all duration-300 hover:scale-[1.04]"
              style={{ borderColor: inst.border }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  background: `${inst.color}18`,
                  border: `1px solid ${inst.border}`,
                  color: inst.color,
                }}
              >
                {inst.icon}
              </div>
              <span
                className="font-semibold text-sm"
                style={{ fontFamily: "Cinzel, serif", color: "#f0f5ff" }}
              >
                {inst.title}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Gallery */}
        <h2
          className="mb-6 font-bold"
          style={{
            fontFamily: "Cinzel, serif",
            color: "#f0f5ff",
            fontSize: "1.1rem",
          }}
        >
          Inside the Music Room
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            "/renamed_website_images/music-guitar-class-1.jpg",
            "/renamed_website_images/music-guitar-class-2.jpg",
            "/renamed_website_images/music-guitar-class-3.jpg",
          ].map((src, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
              style={{
                border: "1px solid rgba(236,72,153,0.2)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              }}
            >
              <img
                src={src}
                alt={`Music Room ${i + 1}`}
                className="w-full object-cover"
                style={{ height: "200px" }}
              />
            </div>
          ))}
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between pt-10 mt-12 border-t border-white/10">
          <Link
            to="/facilities/library"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-card text-xs uppercase tracking-widest text-gold hover:border-gold transition-all"
          >
            <FaArrowLeft /> Previous: School Library
          </Link>
          <Link
            to="/facilities/sports"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full cosmic-btn-gold text-xs uppercase tracking-widest"
          >
            Next: Sports Activities <FaArrowRight />
          </Link>
        </div>
      </div>

      <Link
        to="/facilities/library"
        aria-label="Previous: School Library"
        className="fixed z-50 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowLeft />
      </Link>
      <Link
        to="/facilities/sports"
        aria-label="Next: Sports Activities"
        className="fixed z-50 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowRight />
      </Link>
    </section>
  );
};

export default MusicRoom;
